import * as Sentry from '@sentry/react';
import { FieldValue, db as firestoreDb } from 'firebase-modules';

/**
 * Feature usage telemetry — anonymous aggregate counters.
 *
 * # Schema
 * Events land in `_analytics/usage_YYYY_Www` weekly Firestore docs as
 * `FieldValue.increment(n)` updates. Field key shape:
 *
 *   `event[__label][__multi]`   (double-underscore separator)
 *
 *   - event:  snake_case Object_Verb — `card_moved`, `connection_added`
 *   - label:  variant/context — `context_menu`, `drag_drop`, `module`
 *   - multi:  appended as `__multi` when the action operated on >1 entity
 *
 * Separator is `__` (not `.`) so Firestore's update() treats keys as literal
 * field names, not nested paths. Mixing dot-paths and bare names in the same
 * doc throws TypeError on conflicts.
 *
 * # Taxonomy
 * To keep the catalog grep-able and prevent typo drift, valid events and
 * labels are typed as string-literal unions below. When adding a new
 * instrumentation call, extend `TrackEvent` / `TrackLabel` first — the
 * compiler will then guide every call site.
 *
 * # Reliability
 * Buffer flushes on a 30s interval, on `visibilitychange:hidden`, and on
 * `pagehide` (Safari/Firefox tab close). On Firestore failure we add a
 * Sentry breadcrumb so silent drops show up in error context. The buffer is
 * cleared synchronously before write — so simultaneous failures across both
 * `update()` and `set({merge:true})` will lose those events; the breadcrumb
 * is the signal you'd use to investigate.
 *
 * # Reading the data
 * - MCP tool: `mcp__creative-queue__analyze_feature_usage(weeks)`
 * - MCP tool: `mcp__creative-queue__compare_feature_usage(recent, baseline)`
 * - Script:   `packages/creative-queue-mcp/scripts/query-analytics.ts`
 */

// ---------- Taxonomy ----------

export type TrackEvent =
  // Session lifecycle
  | 'session_created'
  | 'session_opened'
  | 'session_duplicated'
  | 'session_renamed'
  | 'session_archived'
  | 'session_unarchived'
  | 'session_deleted'
  // Card lifecycle
  | 'card_created'
  | 'card_moved'
  | 'card_renamed'
  | 'card_completed'
  | 'card_deleted'
  | 'card_restored'
  | 'card_duplicated'
  | 'card_reordered'
  | 'card_layout_changed'
  // Card metadata
  | 'date_set'
  | 'priority_set'
  | 'issue_resolved'
  | 'issue_flagged'
  | 'content_copied'
  | 'formula_opened'
  | 'layout_opened'
  | 'present_mode_entered'
  | 'present_mode_exited'
  // Module / connection actions
  | 'module_created'
  | 'module_renamed'
  | 'module_source_changed'
  | 'module_deleted'
  | 'connection_added'
  | 'connection_removed'
  | 'connection_filter_toggled'
  // Source CRUD
  | 'source_created'
  | 'source_import_opened'
  | 'source_imported'
  | 'source_import_failed'
  | 'source_rows_imported'
  | 'source_renamed'
  | 'source_duplicated'
  | 'source_deleted'
  // Step CRUD
  | 'step_created'
  | 'step_renamed'
  | 'step_deleted'
  | 'step_reordered'
  // Source grid editing
  | 'cell_edited'
  | 'image_uploaded'
  // Library panel
  | 'library_opened'
  | 'library_mode_changed'
  // View / chrome
  | 'display_mode_changed'
  | 'filter_cleared'
  | 'search_opened'
  | 'add_cards_opened'
  | 'context_menu_opened'
  | 'cards_expanded'
  | 'cards_collapsed'
  // Breakout variations
  | 'breakout_started'
  | 'breakout_executed'
  | 'breakout_cancelled'
  | 'breakout_wizard_step_changed'
  | 'breakout_preview_toggled'
  | 'breakout_accordion_toggled'
  | 'breakout_variant_chip_navigated'
  // Quickstart funnel
  | 'quickstart_started'
  | 'quickstart_step_completed'
  | 'quickstart_completed'
  | 'whole_intro_shown'
  | 'whole_intro_dismissed'
  // Onboarding funnel
  | 'onboarding_started'
  | 'onboarding_step_completed'
  | 'onboarding_completed'
  | 'onboarding_abandoned'
  // Collections (Assets tab)
  | 'collection_sidebar_toggled'
  | 'collection_created'
  | 'collection_filter_applied'
  | 'collection_filter_cleared'
  | 'collection_meta_filter_applied'
  | 'collection_member_added'
  | 'collection_member_status_changed'
  | 'collection_member_removed'
  | 'collection_download_zip'
  | 'collection_deleted'
  | 'collection_duplicated'
  | 'collection_renamed'
  | 'collection_moved'
  | 'assets_group_by_changed'
  | 'assets_date_filter_applied'
  | 'assets_date_filter_cleared'
  // Organization admin
  | 'member_removed'
  | 'member_removal_retried';

/**
 * Closed label vocabulary. Keep this list small — labels are for *how* an
 * action happened (entry point, variant), not *what* it operated on.
 * If you find yourself wanting a label that names an entity, you probably
 * want a new event instead.
 *
 * NOTE: The `(string & {})` fallback at the end gives autocomplete on the
 * canonical set while still allowing freeform labels (numeric priorities,
 * quickstart step names). The tradeoff: typos in canonical labels (e.g.
 * `'modual'` for `'module'`) silently match the string fallback and won't
 * be caught at compile time. Grep before adding a new label to make sure
 * you're not duplicating.
 */
export type TrackLabel =
  // Entry points
  | 'toolbar'
  | 'context_menu'
  | 'drag_drop'
  | 'spring_load'
  | 'keyboard'
  | 'modal'
  | 'badge'
  | 'module'
  | 'expand'
  | 'quickstart'
  | 'duplicate'
  | 'connection'
  | 'add_modal'
  | 'snackbar_retry'
  | 'gallery'
  | 'spreadsheet'
  | 'from_existing'
  | 'from_scratch'
  | 'module_manager'
  // Onboarding completion paths
  | 'upload'
  | 'standard'
  | 'no_sources'
  // Source / display kinds
  | 'image'
  | 'data'
  | 'csv_upload'
  | 'csv_paste'
  | 'csv'
  | 'tsv'
  | 'list'
  | 'grid'
  | 'gallery_lg'
  // Library modes
  | 'select'
  | 'edit'
  | 'browse'
  // Cell field types
  | 'text'
  | 'select_field'
  | 'multiselect'
  | 'date'
  | 'checkbox'
  // Upload kinds
  | 'picker'
  | 'paste'
  // Card actions
  | 'permanent'
  | 'top'
  | 'up_next'
  | 'on_deck'
  | 'bottom'
  | 'url'
  | 'filename'
  | 'title'
  | 'delivery'
  | 'launch'
  | 'internal_review'
  | 'stakeholder_review'
  | 'publish'
  | 'custom'
  | 'manage'
  | 'none'
  | 'card'
  | 'source_toolbar'
  | 'variants_section'
  | 'history_section'
  | 'variant_chip'
  // Collections sidebar
  | 'sidebar'
  | 'floating'
  // Numeric priorities (1-5) and freeform quickstart step names allowed
  // via the string fallback in `trackAction`.
  | (string & {}); // eslint-disable-line @typescript-eslint/ban-types

// ---------- Internals ----------

// Weekly bucket: _analytics/usage_2026_W13
function getWeeklyDocRef() {
  const now = new Date();
  const year = now.getFullYear();
  const week = getISOWeek(now);
  return firestoreDb.collection('_analytics').doc(`usage_${year}_W${week}`);
}

function getISOWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

const buffer: Record<string, number> = {};
const trackOnceFired = new Set<string>();
let flushTimer: ReturnType<typeof setInterval> | null = null;
let listenersAttached = false;

function startBuffer() {
  if (flushTimer) return;
  flushTimer = setInterval(flush, 30_000);
  if (!listenersAttached && typeof window !== 'undefined') {
    listenersAttached = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
    // pagehide fires reliably across Safari/Firefox on tab close & nav,
    // where visibilitychange sometimes doesn't.
    window.addEventListener('pagehide', () => flush());
  }
}

function flush() {
  const snapshot = { ...buffer };
  for (const key of Object.keys(buffer)) delete buffer[key];

  if (Object.keys(snapshot).length === 0) return;

  const updates: Record<string, unknown> = {};
  for (const [key, count] of Object.entries(snapshot)) {
    updates[key] = FieldValue.increment(count);
  }

  const ref = getWeeklyDocRef();
  ref.update(updates).catch(() => {
    // Document doesn't exist yet (first write of the week) — create with literal counts.
    // set+merge with plain numbers is fine here; the firebase-js-sdk#3045 bug only
    // affects FieldValue.increment inside set+merge.
    ref.set(snapshot, { merge: true }).catch((err: unknown) => {
      // Both write paths failed — events are gone. Drop a breadcrumb so this
      // shows up next to whatever else broke in the user's session, instead
      // of being indistinguishable from "no usage."
      Sentry.addBreadcrumb({
        category: 'analytics',
        level: Sentry.Severity.Warning,
        message: 'analytics flush failed',
        data: {
          eventCount: Object.keys(snapshot).length,
          totalFires: Object.values(snapshot).reduce((s, n) => s + n, 0),
          error: err instanceof Error ? err.message : String(err),
        },
      });
    });
  });
}

// ---------- Public API ----------

/**
 * Track a user action. Fire-and-forget — never blocks UI.
 * Events are batched and flushed to Firestore every 30s, on `visibilitychange:hidden`,
 * and on `pagehide`.
 */
export function trackAction(event: TrackEvent, label?: TrackLabel, multi?: boolean) {
  let key = label ? `${event}__${label}` : event;
  if (multi) key += '__multi';
  buffer[key] = (buffer[key] || 0) + 1;
  startBuffer();
}

/**
 * Track an action only the first time per page-session for a given key.
 * Use for milestones (e.g., quickstart_started) so refreshing or re-opening
 * a wizard doesn't inflate the counter.
 */
export function trackOnce(event: TrackEvent, label?: TrackLabel) {
  const dedupeKey = label ? `${event}__${label}` : event;
  if (trackOnceFired.has(dedupeKey)) return;
  trackOnceFired.add(dedupeKey);
  trackAction(event, label);
}

/**
 * Manually flush the analytics buffer. Exposed for DevTools-driven testing
 * (`window.__flushAnalytics()`) and for code paths that know they're about
 * to navigate before the timer fires.
 *
 * NOTE: `window.__flushAnalytics` is a dev-affordance — anyone with a console
 * can call it. Since this is anonymous aggregate data and we already trust the
 * client to count truthfully, the abuse risk is near-zero, but don't rely on
 * these counters for billing or anything load-bearing.
 */
export function flushAnalytics() {
  flush();
}

if (typeof window !== 'undefined') {
  (window as unknown as { __flushAnalytics?: () => void }).__flushAnalytics = flushAnalytics;
}
